import { BpsLogPoint, ChartPoint } from "./types";
import { formatTimeLabel } from "./datetime";

export const REALTIME_WINDOW_MINUTES_OPTIONS = [
  1, 3, 5, 10, 20, 30, 40, 50, 60,
] as const;

// realtime表示の窓外データを切り落として、表示対象の範囲に収める。
export const trimToRealtimeWindow = (
  data: BpsLogPoint[],
  nowMs: number,
  windowMinutes: number,
) => {
  const windowSec = Math.max(1, Math.floor(windowMinutes * 60));
  const endMs = Math.floor(nowMs / 1000) * 1000;
  const displayStartMs = endMs - (windowSec - 1) * 1000;
  // 秒境界ズレ対策で、内部データは1秒余裕を持って保持
  const bufferMs = 1000;
  const keepStartMs = displayStartMs - bufferMs;
  if (data.length === 0) return data;

  let firstInRangeIdx = 0;
  for (let i = 0; i < data.length; i++) {
    const t = new Date(data[i].timestamp).getTime();
    if (!Number.isFinite(t)) {
      return data;
    }
    if (t >= keepStartMs) {
      firstInRangeIdx = i;
      break;
    }
    firstInRangeIdx = data.length - 1;
  }

  if (firstInRangeIdx <= 0) return data;
  return data.slice(firstInRangeIdx);
};

// realtime表示用に、1秒バケツへ整形したチャート点配列を生成する（欠損はnullで埋める）。
export const buildRealtimeChartPoints = (
  bpslogDataForDisplay: BpsLogPoint[],
  realtimeNowMs: number,
  windowMinutes: number,
): ChartPoint[] => {
  const windowSec = Math.max(1, Math.floor(windowMinutes * 60));
  const pointCount = windowSec;

  const endMs = Math.floor(realtimeNowMs / 1000) * 1000;
  const startMs = endMs - (windowSec - 1) * 1000;

  const bucket: Array<{
    l1: number | null;
    l2: number | null;
    l3: number | null;
    l4: number | null;
    actual: number | null;
  } | null> = Array.from({ length: pointCount }, () => null);

  for (const pt of bpslogDataForDisplay) {
    const t = new Date(pt.timestamp).getTime();
    if (!Number.isFinite(t)) continue;
    const idx = Math.floor((t - startMs) / 1000);
    if (idx < 0 || idx >= pointCount) continue;
    bucket[idx] = {
      l1: typeof pt.line1_bitrate === "number" ? pt.line1_bitrate : null,
      l2: typeof pt.line2_bitrate === "number" ? pt.line2_bitrate : null,
      l3: typeof pt.line3_bitrate === "number" ? pt.line3_bitrate : null,
      l4: typeof pt.line4_bitrate === "number" ? pt.line4_bitrate : null,
      actual: typeof pt.actual_bitrate === "number" ? pt.actual_bitrate : null,
    };
  }

  const chartPoints: ChartPoint[] = [];
  for (let i = 0; i < pointCount; i++) {
    const tMs = startMs + i * 1000;
    const ts = new Date(tMs).toISOString();
    const v = bucket[i];
    chartPoints.push({
      label: formatTimeLabel(ts, false),
      originalIdx: null,
      timestamp: ts,
      xMs: tMs,
      l1: v?.l1 ?? null,
      l2: v?.l2 ?? null,
      l3: v?.l3 ?? null,
      l4: v?.l4 ?? null,
      actual: v?.actual ?? null,
    });
  }

  return chartPoints;
};
